import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, type DragEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMatch } from "react-router";
import { ROUTE_PATTERNS } from "../../../shared/routing/appPaths";
import { useProjects } from "../../../projects/projectContext";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { useMemberNames } from "../data/useMemberNames";
import { useProjectThreads } from "../data/useProjectThreads";
import { useThreadPlacements } from "../data/useThreadPlacements";
import HostOfferInbox from "../hosting/HostOfferInbox";
import { useNewThread } from "../prepare";
import NewThreadBlocked, { CoreReadOnlyReason } from "./NewThreadBlocked";
import { useReviewInbox } from "../../reviews";
import { inboxSubjects } from "./inboxRuns";
import ProjectInbox from "./ProjectInbox";
import SidebarShelf from "./SidebarShelf";
import ThreadListRow from "./ThreadListRow";
import ThreadSearchResults from "./ThreadSearchResults";
import Field from "../../../ui/forms/Field";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ErrorNotice from "../../../errors/ErrorNotice";
import { threadSections, type PlacedThread, type ThreadSection } from "./threadSections";
import { pinRowTarget, planPinMove, planThreadDrop, type DropSection, type DropTarget } from "./threadDrops";

const SEARCH_DELAY_MILLISECONDS = 300;
const MINIMUM_QUERY_LENGTH = 2;

/**
 * The Threads section's sidebar, as T3 Code's: start, search, the inbox (what waits for you;
 * F108, "The sidebar IS the Inbox") and the project's Threads by section, Shared among them
 * (F109).
 */
export default function ThreadsSidebar() {
  const { t } = useTranslation();
  const projects = useProjects();
  const projectId = projects.selectedProjectId;
  const people = useMemberNames(projectId);
  const [queryText, setQueryText] = useState("");
  const [query, setQuery] = useState("");
  const newThread = useNewThread(projectId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(queryText.trim());
    }, SEARCH_DELAY_MILLISECONDS);
    return () => {
      clearTimeout(timer);
    };
  }, [queryText]);

  // Without a project the page itself says what to do; the sidebar doesn't repeat it (TR-A2-02).
  if (projectId === null) return null;

  const searching = query.length >= MINIMUM_QUERY_LENGTH;
  return (
    <div className="space-y-3 px-3 py-4">
      {newThread.status === "checking" ? null : newThread.status === "failed" ? (
        <NewThreadBlocked status="failed" decision={newThread.decision} />
      ) : (
        // Never hidden without saying why (F107, P-B): where it can't start, it stays and says so;
        // a missing permission locks it and opens what would open it (GS-8).
        <AppButton
          variant="primary"
          size="sm"
          fullWidth={true}
          onClick={newThread.start}
          requires={newThread.status === "notPermitted" ? newThread.decision : undefined}
          unavailable={newThread.status === "coreReadOnly" ? <CoreReadOnlyReason /> : undefined}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          {t("threads.prepare.run.newThread")}
        </AppButton>
      )}
      <Field labelHidden label={t("threads.search.label")}>
        {(control) => (
          <div className="relative">
            <MagnifyingGlassIcon className="app-icon-subtle pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden="true" />
            <input
              {...control}
              type="search"
              value={queryText}
              onChange={(event) => { setQueryText(event.target.value); }}
              placeholder={t("threads.search.placeholder")}
              className={`${FORM_COMPACT_CONTROL_CLASS} pl-7`}
            />
          </div>
        )}
      </Field>
      <HostOfferInbox />
      <ProjectInbox projectId={projectId} />
      {searching ? (
        <ThreadSearchResults query={query} nameOf={people.nameOf} />
      ) : (
        <ProjectThreads projectId={projectId} currentUserId={people.currentUserId} nameOf={people.nameOf} />
      )}
    </div>
  );
}

interface ProjectThreadsProps {
  projectId: string;
  currentUserId: string | null;
  nameOf: (userId: string) => string;
}

function ProjectThreads({ projectId, currentUserId, nameOf }: ProjectThreadsProps) {
  const { t } = useTranslation();
  const threadMatch = useMatch(ROUTE_PATTERNS.thread);
  const offerMatch = useMatch(ROUTE_PATTERNS.threadHostOffer);
  const selectedThreadId = threadMatch?.params.threadId ?? offerMatch?.params.threadId ?? null;
  const { state, reload } = useProjectThreads(projectId);
  const placements = useThreadPlacements(projectId);
  // The Runs the inbox above leads into, listed there once (run-frame P3).
  const inbox = useReviewInbox(projectId);
  const inInbox = inbox?.status === "loaded" ? inboxSubjects(inbox.value) : undefined;
  const [dragged, setDragged] = useState<string | null>(null);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />;
  if (state.value.length === 0) {
    return <p className="app-text-muted px-2 py-3 text-xs">{t("threads.list.empty")}</p>;
  }
  const sections = threadSections(state.value, placements.byThread, placements.now, currentUserId, inInbox);
  const pins = sections.pinned.map((pin) => pin.summary.threadId);
  const dropOn = (target: DropTarget) => {
    if (dragged !== null) placements.drop(planThreadDrop(sections, dragged, target));
    setDragged(null);
  };
  const dropZone = (section: DropSection) => ({
    onDragOver: (event: DragEvent) => { if (dragged !== null) event.preventDefault(); },
    onDrop: (event: DragEvent) => { event.preventDefault(); dropOn({ section, beforeThreadId: null }); },
  });
  const rows = (threads: readonly PlacedThread[], section: ThreadSection) => (
    <ul className="space-y-0.5">
      {threads.map(({ summary, place, placement }) => (
        <ThreadListRow
          key={summary.threadId}
          summary={summary}
          hostName={summary.host === currentUserId ? null : nameOf(summary.host)}
          selected={summary.threadId === selectedThreadId}
          place={place}
          snoozedUntil={placement?.snoozedUntil ?? null}
          pinned={placement?.pinnedAt != null}
          onPlace={(event, until) => { placements.place(summary.threadId, event, until); }}
          drag={
            section === "working"
              ? undefined
              : {
                  onStart: () => { setDragged(summary.threadId); },
                  onEnd: () => { setDragged(null); },
                  ...(section === "pinned"
                    ? {
                        onDropOnRow: (upperHalf: boolean) => { dropOn(pinRowTarget(sections, summary.threadId, upperHalf)); },
                        onMove: (direction: "up" | "down") => { placements.drop(planPinMove(sections, pins, summary.threadId, direction)); },
                      }
                    : {}),
                }
          }
        />
      ))}
    </ul>
  );
  return (
    <nav aria-label={t("threads.list.title")} className="space-y-2">
      {placements.error === null ? null : <ErrorNotice error={placements.error} compact={true} />}
      {sections.pinned.length === 0 ? (
        dragged === null ? null : (
          <p {...dropZone("pinned")} className="app-text-muted rounded border border-dashed px-2 py-2 text-xs">
            {t("threads.list.placement.dropToPin")}
          </p>
        )
      ) : (
        <section {...dropZone("pinned")} aria-label={t("threads.list.placement.pinned")} className="app-divider-bottom border-b pb-1">
          {rows(sections.pinned, "pinned")}
        </section>
      )}
      <div {...dropZone("active")} className={dragged === null ? undefined : "min-h-6 rounded outline-dashed outline-1"}>
        {rows(sections.active, "active")}
      </div>
      {SHELVES.map((shelf) => (
        <Shelf
          key={shelf}
          shelf={shelf}
          threads={sections[shelf]}
          selectedThreadId={selectedThreadId}
          dropZone={shelf === "settled" && dragged !== null ? dropZone("settled") : undefined}
        >
          {(threads) => rows(threads, shelf)}
        </Shelf>
      ))}
    </nav>
  );
}

/** T3 Code's shelves under the active list, each collapsed until opened; Shared is one (F109). */
const SHELVES = ["working", "shared", "snoozed", "settled"] as const satisfies readonly ThreadSection[];

interface ShelfProps {
  shelf: (typeof SHELVES)[number];
  threads: readonly PlacedThread[];
  selectedThreadId: string | null;
  /** While a Thread is dragged: Settled takes it (T3 Code settles a Thread dropped there). */
  dropZone?: { onDragOver: (event: DragEvent) => void; onDrop: (event: DragEvent) => void } | undefined;
  children: (threads: readonly PlacedThread[]) => ReactNode;
}

/**
 * Working, Snoozed or Settled Threads, as T3 Code's shelves: collapsed by default, kept open
 * or closed in this browser, and while collapsed still showing the Thread that is open.
 */
function Shelf({ shelf, threads, selectedThreadId, dropZone, children }: ShelfProps) {
  const { t } = useTranslation();
  if (threads.length === 0 && dropZone === undefined) return null;
  return (
    <SidebarShelf storageKey={`dmw.threads.shelf.${shelf}`} title={t(`threads.list.placement.${shelf}`, { count: threads.length })} dropZone={dropZone}>
      {(open) => {
        const shown = open ? threads : threads.filter((thread) => thread.summary.threadId === selectedThreadId);
        return shown.length === 0 ? null : children(shown);
      }}
    </SidebarShelf>
  );
}
